import { Action, createReducer, on } from '@ngrx/store';
import {
  Activity,
  ActivityLoaded,
  ActivityStudentAnswers,
  ActivityStudentAverage,
  AverageGrade,
  ErrorModel,
  Feature,
  Materials,
  ProgressActivityModel,
  Resources,
  Rubric,
  RubricsAnswers,
  SelfAssessment,
  StepsCategoryModel,
  Tags,
  TeachingInfo,
} from '@tekman/onmat/models';
import * as ActivitiesActions from './activities.actions';

export const ACTIVITIES_FEATURE_KEY = 'activities';

export interface State {
  loaded: ActivityLoaded;
  activity: Activity;
  solutionary?: string;
  averageGrades?: AverageGrade[];
  rubrics?: Rubric[];
  tags?: Tags[];
  features?: Feature[];
  status?: number;
  resources?: Resources;
  materials?: Materials;
  rubricsMultiChoice?: RubricsAnswers;
  answers?: ActivityStudentAnswers[];
  averages?: ActivityStudentAverage;
  selfAssessment?: SelfAssessment[];
  exercisesProgress: ProgressActivityModel[];
  teachingInfo: TeachingInfo;
  steps: StepsCategoryModel;
  error?: ErrorModel;
}

export interface ActivitiesPartialState {
  readonly [ACTIVITIES_FEATURE_KEY]: State;
}

export const initialState: State = {
  loaded: {
    activity: false,
    steps: false,
    rubrics: false,
    tags: false,
    evaluationPages: false,
    averageGrades: false,
    features: false,
    subcompetences: false,
    teachingInfo: false,
    teacherGuide: false,
    teacherSolutionary: false,
    saveEvaluationPages: false,
    publish: false,
    loadActivityAnswers: false,
    materials: false,
    saveFeature: false,
    multianswer: false,
  },
  activity: null,
  steps: null,
  error: null,
  exercisesProgress: [],
  teachingInfo: undefined,
};

const activitiesReducer = createReducer(
  initialState,
  on(ActivitiesActions.loadTeacherActivitySuccess, (state, { activity }) => ({
    ...state,
    activity,
    loaded: { ...state.loaded, activity: true },
  })),
  on(ActivitiesActions.loadTeacherStepsSuccess, (state, { steps }) => {
    return {
      ...state,
      steps,
      loaded: { ...state.loaded, steps: true },
    };
  }),
  on(ActivitiesActions.loadTeacherActivityRubricsSuccess, (state, { rubrics }) => ({
    ...state,
    rubrics,
    loaded: { ...state.loaded, rubrics: true },
  })),
  on(ActivitiesActions.loadTeacherTagsSuccess, (state, { tags }) => ({
    ...state,
    tags: tags,
    loaded: { ...state.loaded, tags: true },
  })),
  on(ActivitiesActions.loadTeachingInfoSuccess, (state, { teachingInfo }) => ({
    ...state,
    teachingInfo: teachingInfo,
    loaded: { ...state.loaded, teachingInfo: true },
  })),
  on(ActivitiesActions.saveAverageGradesSuccess, (state, { averageGrades }) => ({
    ...state,
    averageGrades,
    loaded: { ...state.loaded, averageGrades: true },
  })),
  on(ActivitiesActions.addTeacherFeatures, (state, { features }) => ({
    ...state,
    features,
    loaded: { ...state.loaded, features: true },
  })),
  on(ActivitiesActions.loadEvaluationStudentSubcompetenceStatusSuccess, (state, { groupEvaluationSubcompetenceStatus }) => ({
    ...state,
    activity: {
      ...state.activity,
      groupEvaluationStatus: groupEvaluationSubcompetenceStatus,
    },
    loaded: { ...state.loaded, subcompetences: true },
  })),
  on(ActivitiesActions.loadTeacherGuideSuccess, (state, { exerciseId, teacherGuide }) => ({
    ...state,
    activity: {
      ...state.activity,
      exercises: [...state.activity.exercises].map(exercise => {
        if (exercise.id !== exerciseId) {
          return exercise;
        }
        return {
          ...exercise,
          teacherGuide,
        };
      }),
    },
    loaded: { ...state.loaded, teacherGuide: true },
  })),
  on(ActivitiesActions.loadSolutionarySuccess, (state, { exerciseId, solutionary }) => ({
    ...state,
    activity: {
      ...state.activity,
      exercises: [...state.activity.exercises].map(exercise => {
        if (exercise.id !== exerciseId) {
          return exercise;
        }
        return {
          ...exercise,
          solutionary,
        };
      }),
    },
    loaded: { ...state.loaded, teacherSolutionary: true },
  })),
  on(ActivitiesActions.saveEvaluationPagesSuccess, (state, { evaluationPage }) => ({
    ...state,
    activity: {
      ...state.activity,
      exercises: [...state.activity.exercises].map(exercise => {
        if (exercise.id !== evaluationPage.exerciseId) {
          return exercise;
        }
        return {
          ...exercise,
          isHidden: !evaluationPage.isEvaluable,
          isEvaluable: evaluationPage.isEvaluable,
        };
      }),
    },
    loaded: { ...state.loaded, saveEvaluationPages: true },
  })),
  on(ActivitiesActions.updateStudent, (state, { status, selectedStudent, grades }) => ({
    ...state,
    activity: {
      ...state.activity,
      groupEvaluationStatus: {
        countDelivered: state.activity.groupEvaluationStatus.countDelivered,
        countPending: state.activity.groupEvaluationStatus.countPending,
        studentsStatus: [...state.activity.groupEvaluationStatus.studentsStatus].map(studentStatus => {
          if (studentStatus.studentId !== selectedStudent) {
            return studentStatus;
          }
          return {
            ...(studentStatus = {
              ...studentStatus,
              activityStatus: status,
              state: status,
              grade1: grades[0].grade,
              grade2: grades.length > 1 ? grades[1].grade || '-' : null,
            }),
          };
        }),
      },
    },
    loaded: { ...state.loaded, publish: true },
  })),
  on(ActivitiesActions.repeatActivitySuccess, (state, { selectedStudent, status }) => ({
    ...state,
    activity: {
      ...state.activity,
      groupEvaluationStatus: {
        countDelivered: state.activity.groupEvaluationStatus.countDelivered - 1,
        countPending: state.activity.groupEvaluationStatus.countPending + 1,
        studentsStatus: [...state.activity.groupEvaluationStatus.studentsStatus].map(student => {
          if (student.studentId !== selectedStudent) {
            return student;
          }
          const out_student = {
            ...student,
            activityStatus: status,
            state: status,
            grade1: '-',
            grade2: null,
            subCompetenceGrade: student.subCompetenceGrade.map(subcompetence => {
              return (subcompetence = {
                ...subcompetence,
                grade: null,
              });
            }),
          };

          return out_student;
        }),
      },
    },
  })),
  on(ActivitiesActions.loadStudentAnswersAPracticarSuccess, (state, { rubricsMultiChoice }) => ({
    ...state,
    rubricsMultiChoice,
    loaded: { ...state.loaded, multianswer: true },
  })),
  on(ActivitiesActions.saveFeatureSuccess, (state, { features }) => ({
    ...state,
    features,
    loaded: { ...state.loaded, saveFeature: true },
  })),
  on(ActivitiesActions.modifyFeatureSuccess, (state, { activityId, featureName, value }) => ({
    ...state,
    steps: {
      ...state.steps,
      categories: [...state.steps.categories].map(category => {
        return {
          ...category,
          activities: [...category.activities].map(activity => {
            if (activity.id !== activityId) {
              return activity;
            }
            return {
              ...activity,
              isHidden: value,
            };
          }),
        };
      }),
    },
  })),
  on(ActivitiesActions.loadActivityMaterialsSuccess, (state, { materials }) => ({
    ...state,
    materials,
    loaded: { ...state.loaded, materials: true },
  })),
  on(ActivitiesActions.loadActivityStudentAnswersSuccess, (state, { answers, averages }) => ({
    ...state,
    answers,
    averages,
    loaded: { ...state.loaded, loadActivityAnswers: true },
  })),
  on(
    ActivitiesActions.loadTeacherActivityFailure,
    ActivitiesActions.loadTeacherStepsFailure,
    ActivitiesActions.loadTeacherActivityRubricsFailure,
    ActivitiesActions.loadTeacherTagsFailure,
    ActivitiesActions.loadEvaluationPagesFailure,
    ActivitiesActions.loadTeachingInfoFailure,
    ActivitiesActions.saveAverageGradesFailure,
    ActivitiesActions.loadEvaluationStudentSubcompetenceStatusFailure,
    ActivitiesActions.applySubcompetencesAssessmentStudentsFailure,
    ActivitiesActions.loadSolutionaryFailure,
    ActivitiesActions.loadTeacherGuideFailure,
    ActivitiesActions.saveEvaluationPagesFailure,
    ActivitiesActions.repeatActivityFailure,
    ActivitiesActions.loadStudentAnswersAPracticarFailure,
    ActivitiesActions.saveFeatureFailure,
    ActivitiesActions.loadActivityMaterialsFailure,
    ActivitiesActions.loadActivityStudentAnswersFailure,
    (state, error) => ({
      ...state,
      error: error,
      loaded: { ...state.loaded, [error.type]: false },
    })
  ),
  on(ActivitiesActions.clearActivity, () => initialState)
);

export function reducer(state: State | undefined, action: Action): State {
  return activitiesReducer(state, action);
}
